import { Component, HostListener, OnInit, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BaseService } from '../../base.service';
import { UserAuthService } from '../../auth.service';
import { SPECTRUM_API_PATH, SPECTRUM_AUTH_API } from '../../tokens';

interface SchoolAccess {
    orgCode: string;
    schoolName: string;
    employeeName: string;
    userUniqueId: string;
    username: string;
    imageSrc?: string;
    logo?: string;
    logoUrl?: string;
    logoURL?: string;
}

@Component({
    selector: 'spectrum-school-change',
    imports: [CommonModule, FormsModule],
    templateUrl: './school-change.component.html',
    styleUrl: './school-change.component.scss'
})
export class SpectrumSchoolChangeComponent implements OnInit {

    @Output() closed = new EventEmitter<void>();

    private base = inject(BaseService);
    private auth = inject(UserAuthService);
    private apiPath = inject(SPECTRUM_API_PATH);
    private authBase = inject(SPECTRUM_AUTH_API);

    schools: SchoolAccess[] = [];
    selected: SchoolAccess | null = null;
    loading = true;
    searchText = '';

    get currentOrgCode(): string | null {
        return this.auth.getOrgCode();
    }

    get filteredSchools(): SchoolAccess[] {
        const q = this.searchText.trim().toLowerCase();
        if (!q) return this.schools;
        return this.schools.filter(school =>
            school.schoolName?.toLowerCase().includes(q) ||
            school.employeeName?.toLowerCase().includes(q) ||
            school.orgCode?.toLowerCase().includes(q)
        );
    }

    ngOnInit(): void {
        const userName = this.auth.getUserName();
        this.base.get<SchoolAccess[]>(`${this.authBase}/auth/user-access?username=${userName}&appName=Spectrum`)
            .subscribe({
                next: res => {
                    this.schools = res || [];
                    this.selected = this.schools.find(s => s.orgCode === this.currentOrgCode) || null;
                    this.loading = false;
                    this.loadSchoolLogos();
                },
                error: () => { this.loading = false; }
            });
    }

    select(school: SchoolAccess): void {
        this.selected = school;
    }

    confirm(): void {
        if (!this.selected) return;
        const s = this.selected;
        this.auth.saveOrgCode(s.orgCode);
        this.auth.saveSchoolName(s.schoolName);
        this.auth.saveEmployeeName(s.employeeName);
        this.auth.saveEmployeeId(s.userUniqueId);
        const params = new URLSearchParams({
            token: this.auth.getToken() || '',
            code: s.orgCode || '',
            appName: 'Spectrum',
            employeeId: s.userUniqueId || '',
            employeeName: s.employeeName || '',
            schoolName: s.schoolName || '',
            userName: this.auth.getUserName() || ''
        });
        window.location.href = `${window.location.origin}/validate?${params.toString()}`;
    }

    close(): void {
        this.closed.emit();
    }

    @HostListener('document:keydown.escape')
    closeOnEscape(): void {
        this.close();
    }

    getInitials(name: string): string {
        if (!name) return '?';
        return name.trim().split(' ').filter(Boolean).map(n => n[0]).join('').substring(0, 2).toUpperCase();
    }

    getSchoolLogo(school: SchoolAccess): string {
        return school.imageSrc || school.logoUrl || school.logoURL || school.logo || '';
    }

    private loadSchoolLogos(): void {
        this.schools.forEach(school => {
            if (!school.orgCode || this.getSchoolLogo(school)) return;

            const params = new URLSearchParams({
                module: 'School',
                fileName: 'logo',
                schoolCode: school.orgCode
            });

            this.base.get<{ URL?: string; url?: string }>(`${this.apiPath}/documents/get-sysadmin?${params.toString()}`)
                .subscribe({
                    next: res => { school.logoUrl = res?.URL || res?.url || ''; },
                    error: () => {}
                });
        });
    }

    handleLogoError(school: SchoolAccess): void {
        school.imageSrc = '';
        school.logoUrl = '';
        school.logoURL = '';
        school.logo = '';
    }
}
